<template>
  <div
    class="checkbox-list"
    :class="{'checkbox-list--columns': columns > 0}"
    @change="emitChanges"
  >
    <template v-if="columns === 1">
      <div
        v-for="optionsValue in visibleOptions"
        :key="optionsValue"
        class="f-field f-field--checkbox"
      >
        <input
          :id="`checkbox-list-${fieldName}-${optionsValue}`"
          v-model="optionsMap[optionsValue]"
          class="f-field__checkbox"
          type="checkbox"
        >
        <label
          class="f-field__label"
          :for="`checkbox-list-${fieldName}-${optionsValue}`"
        >
          {{ $t(`${fieldName}.${optionsValue}`) }}
        </label>
      </div>
    </template>
    <template v-else>
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="checkbox-list"
        :class="`cols-${columns}`"
      >
        <div
          v-for="optionKey in row"
          :key="optionKey"
          class="f-field f-field--checkbox"
        >
          <input
            :id="`checkbox-list-${fieldName}-${optionKey}`"
            v-model="optionsMap[optionKey]"
            class="f-field__checkbox"
            type="checkbox"
          >
          <label
            class="f-field__label"
            :for="`checkbox-list-${fieldName}-${optionKey}`"
          >
            {{ $t(`${fieldName}.${optionKey}`) }}
          </label>
        </div>
      </div>
    </template>
    <button
      v-if="shouldShowExpandButton"
      type="button"
      class="checkbox-list__expand-button"
      @click="isExpanded = !isExpanded"
    >
      {{ isExpanded ? $t('actions.collapse') : $t('actions.expand') }}

      <span
        class="checkbox-list__expand-icon"
        :class="{ 'checkbox-list__expand-icon--rotated': isExpanded }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M11 7L8 10L5 7"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg> </span>
    </button>
  </div>
</template>
<script>
import underscore from 'underscore'

export default {
  props: {
    fieldName: {
      type: String,
      required: true
    },
    modelValue: {
      type: Array,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    maxVisible: {
      type: Number,
      default: 5
    },
    columns: {
      type: Number,
      default: 1,
      validator: prop => Number.isInteger(prop) && prop > 0 && prop < 4
    }
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      optionsMap: this.options.reduce((acc, type) => {
        acc[type] = false
        return acc
      }, {}),
      isExpanded: false
    }
  },
  computed: {
    visibleOptions() {
      // If expanded, show all options
      if (this.isExpanded || this.maxVisible >= this.options.length) {
        return this.options
      }
      // If collapsed, show only up to maxVisible
      return this.options.slice(0, this.maxVisible)
    },
    shouldShowExpandButton() {
      return this.options.length > this.maxVisible
    },
    rows() {
      if (this.columns === 1) {
        return this.visibleOptions
      }
      return underscore.chunk(this.visibleOptions, this.columns)
    },
    selectedOptions() {
      return Object.keys(this.optionsMap).filter(key => this.optionsMap[key])
    }
  },
  watch: {
    modelValue(newVal) {
      const newOptionsMap = this.options.reduce((acc, type) => {
        acc[type] = false
        return acc
      }, {})

      newVal.forEach((type) => {
        newOptionsMap[type] = true
      })

      this.optionsMap = newOptionsMap
    }
  },
  created() {
    this.modelValue.forEach((type) => {
      this.optionsMap[type] = true
    })
  },
  methods: {
    emitChanges() {
      this.$emit('input', this.selectedOptions)
      this.$emit('update:modelValue', this.selectedOptions)
    }
  }
}
</script>
<style lang="scss">
.checkbox-list--columns {
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  .cols-2,
  .cols-3,
  .cols-4,
  .cols-5,
  .cols-6 {
    width: 100%;
  }
  @media screen and (width >= 75rem) {
    .cols-2 {
      width: 50%;
    }
    .cols-3 {
      width: 33.33%;
    }
    .cols-4 {
      width: 25%;
    }
    .cols-5 {
      width: 20%;
    }
    .cols-6 {
      width: 16.66%;
    }
  }
}

.checkbox-list__expand-button {
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.5rem;
  color: #0069E0;
  cursor: pointer;
  font-size: inherit;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.checkbox-list__expand-icon {
  display: flex;
  align-items: center;
  transition: transform 0.4s ease-in-out;
  transform: rotate(0deg);
  svg {
    width: 1em;
    height: 1em;

    path {
      stroke: currentcolor;
    }
  }
}

.checkbox-list__expand-icon--rotated {
  transform: rotate(180deg);
}
</style>
